import { type ReactNode, useEffect, useState } from 'react';
import { useStore } from 'zustand';
import { Link, useMatch, useRouterState } from '@tanstack/react-router';
import * as Popover from '@radix-ui/react-popover';
import { ConversationRail } from '@widgets/conversation-rail';
import { useSession, signOut } from '@entities/session';
import { useGroups, isViewOnlyMember, type Group } from '@entities/group';
import { useCurrencies } from '@entities/currency';
import {
  usePlanUsage,
  planUsageManager,
  planUsageStore,
  PlanUsageLimitModal,
} from '@entities/plan-usage';
import { AddExpenseButton, Avatar, IconButton } from '@ui';
import { ChevronDown, LogoutIcon, Menu } from '@ui/icons';
import { NewBillModal } from '@features/add-expense';
import { GroupSearchInput } from '@features/search-groups';
import { t } from '@shared/i18n';
import { formatTimeZone } from '@shared/lib';
import { toast } from '@shared/lib/toast';
import RilomaMark from '@shared/assets/Images/RilomaAppIcon.svg?react';
import { SideMenu } from './SideMenu';
import { useSessionRevocation } from './useSessionRevocation';

/**
 * App shell shared by every internal page: a global header (hamburger → side-menu drawer, brand,
 * search, Add expense, account menu) + main pane + persistent right "My Groups" rail (Figma layout).
 */
export function AppShell({ children }: { children: ReactNode }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [billOpen, setBillOpen] = useState(false);

  useSessionRevocation();

  // Keep the plan-usage manager store in sync with the server state. Refetches automatically on
  // window focus (TanStack Query default), giving the same "page refresh updates" behavior as mobile.
  const { data: planUsage } = usePlanUsage();
  useEffect(() => {
    if (planUsage) planUsageManager.setUsage(planUsage);
  }, [planUsage]);
  // Pages can opt out of the "My Groups" rail via route `staticData.hideRail` (full-width content),
  // or place it on the right of the main content via `staticData.railRight` (default: left).
  const hideRail = useRouterState({
    select: (state) => state.matches.some((match) => match.staticData.hideRail),
  });
  const railRight = useRouterState({
    select: (state) => state.matches.some((match) => match.staticData.railRight),
  });
  // While viewing a specific group's page (the rail highlights it there), pre-select that group in
  // a NEW bill opened from the global "Add expense" button — instead of always starting blank.
  const groupRouteMatch = useMatch({
    from: '/_authenticated/groups/$groupId',
    shouldThrow: false,
  });
  // A closed group rejects changes (mobile parity): "Add expense" on its page surfaces the
  // closed-group message instead of opening the New Bill modal.
  const { data: groups } = useGroups();
  const { data: sessionUser } = useSession();
  const routeGroup = groupRouteMatch
    ? groups?.find((group) => group.id === groupRouteMatch.params.groupId)
    : undefined;
  const openNewBill = () => {
    if (routeGroup?.isClosed) {
      toast.error(t.shell.closedGroupMessage);
      return;
    }
    // A view-only member can't add expenses to the group they're viewing — surface the
    // permission message instead of opening the New Bill modal (same pattern as closed groups).
    if (routeGroup && sessionUser && isViewOnlyMember(routeGroup, sessionUser.id)) {
      toast.error(t.common.viewOnlyPermission);
      return;
    }
    setBillOpen(true);
  };

  const rail = hideRail ? null : (
    <aside className={`hidden w-rail shrink-0 pb-5 lg:block ${railRight ? 'pr-5' : 'pl-5'}`}>
      <ConversationRail />
    </aside>
  );

  return (
    <div className="flex h-full w-full flex-col overflow-hidden bg-canvas">
      <TopBar onMenuClick={() => setMenuOpen(true)} onAddExpense={openNewBill} />
      <SideMenu open={menuOpen} onOpenChange={setMenuOpen} />
      <NewBillModal
        open={billOpen}
        onOpenChange={setBillOpen}
        {...(groupRouteMatch ? { initialGroupId: groupRouteMatch.params.groupId } : {})}
      />
      <PlanUsageLimitModal />
      <div className="flex min-h-0 flex-1">
        {railRight ? null : rail}
        <main
          className={`flex min-w-0 flex-1 flex-col ${
            railRight ? 'overflow-y-auto lg:overflow-y-hidden' : 'overflow-y-auto'
          }`}
        >
          {children}
        </main>
        {railRight ? rail : null}
      </div>
    </div>
  );
}

function TopBar({
  onMenuClick,
  onAddExpense,
}: {
  onMenuClick: () => void;
  onAddExpense: () => void;
}) {
  const [query, setQuery] = useState('');

  return (
    <header className="flex items-center justify-between bg-transparent px-5 py-5">
      <div className="flex w-[353.667px] items-center gap-5">
        <IconButton
          aria-label={t.shell.openMenu}
          variant="ghost"
          icon={<Menu className="size-6" />}
          onClick={onMenuClick}
          className="rounded-full"
        />
        <Link to="/" aria-label={t.shell.brand} className="flex items-center gap-2">
          <RilomaMark aria-hidden className="h-10.5 w-auto shrink-0 rounded-none" />
          <span className="hidden text-2xl font-normal text-black sm:inline">{t.shell.brand}</span>
        </Link>
      </div>

      <div className="mx-auto hidden w-full max-w-100 md:block">
        <GroupSearchInput
          aria-label={t.shell.search.label}
          placeholder={t.shell.search.placeholder}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onClear={() => setQuery('')}
          className="h-10.5 w-full border border-border-soft bg-surface-raised px-4 text-[14px] tracking-[-0.28px] text-muted-soft shadow-none"
        />
      </div>

      <div className="flex shrink-0 items-center justify-end gap-5 w-[353.667px]">
        <AddExpenseButton
          onClick={onAddExpense}
          className="h-10.25 w-10.25 sm:w-33.5 shrink-0 px-0 sm:px-2 text-[14px]"
        >
          <span className="hidden sm:inline">{t.shell.addExpense}</span>
        </AddExpenseButton>
        <UserMenu />
      </div>
    </header>
  );
}

/**
 * The current user's avatar URL. The login payload carries no picture, so fall back to the URL the
 * backend exposes on the user's group memberships (the same profile picture, resolved by user id).
 */
function resolveAvatarUrl(
  userId: string,
  sessionAvatar: string,
  groups: readonly Group[] | undefined,
): string | undefined {
  if (sessionAvatar) return sessionAvatar;
  for (const group of groups ?? []) {
    const me = group.members.find((m) => m.id === userId);
    if (me?.avatarUrl) return me.avatarUrl;
  }
  return undefined;
}

function UserMenu() {
  const { data: user } = useSession();
  const { data: groups } = useGroups();
  const { data: currencies } = useCurrencies();
  const isPro = useStore(planUsageStore, (s) => s.usage?.isPro ?? false);
  const a = t.shell.account;

  if (!user) return null;

  const avatarUrl = resolveAvatarUrl(user.id, user.avatarUrl, groups);
  // Figma/mobile profile card formats: "Indian rupee (₹ - INR)" and "(GMT+05:30) Mumbai". The
  // /me payload only carries the currency's ID — resolve it against the currencies list; until
  // that loads (or for an id the list doesn't know) fall back to the session's own value.
  const defaultCurrency = currencies?.find((c) => c.id === user.defaultCurrencyId);
  const currencyDisplay = defaultCurrency
    ? `${defaultCurrency.name} (${defaultCurrency.symbol} - ${defaultCurrency.code})`
    : user.defaultCurrency;
  const timeZoneDisplay = formatTimeZone(user.timeZone);

  // Truncate the trigger name to the first word + initial (Figma: "John C.").
  const [first, ...rest] = user.name.trim().split(/\s+/);
  const shortName = rest.length ? `${first} ${rest[rest.length - 1]?.[0] ?? ''}.` : (first ?? '');

  return (
    <Popover.Root>
      <Popover.Trigger asChild>
        <button
          type="button"
          aria-label={a.menuLabel}
          className="flex shrink-0 cursor-pointer items-center gap-[17px] rounded-full bg-surface-raised py-1 pl-1 pr-3 shadow-[0_0_0_1px_var(--color-border)] hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
        >
          <span className="flex items-center gap-2">
            <Avatar
              name={user.name}
              className="size-[34px]"
              {...(avatarUrl ? { src: avatarUrl } : {})}
            />
            <span className="hidden text-[14px] font-medium tracking-[-0.28px] text-fg sm:inline">
              {shortName}
            </span>
          </span>
          <ChevronDown className="size-[14px] text-fg" />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="end"
          sideOffset={10}
          collisionPadding={8}
          className="z-50 w-[calc(100vw-1rem)] max-w-[580px] overflow-hidden rounded-xl border border-border bg-surface-raised shadow-overlay focus:outline-none sm:w-[580px]"
        >
          {/* Identity + profile fields */}
          <div className="p-8 pb-7 flex flex-col">
            <div className="flex items-center gap-4">
              <span className="flex rounded-full p-1 size-24 justify-center items-center bg-linear-to-r from-[#BCA6FF] to-[#A6F131]">
                <Avatar
                  name={user.name}
                  className="size-full"
                  {...(avatarUrl ? { src: avatarUrl } : {})}
                />
              </span>
              <h3 className="text-2xl font-bold text-fg-strong">{user.name}</h3>
              {isPro ? (
                <span className="rounded-full bg-[linear-gradient(203deg,#f7c873_50%,#e0a040_50%)] px-3 py-1 text-xs font-bold tracking-wide text-(--color-text)">
                  PRO
                </span>
              ) : null}
            </div>

            <dl className="mt-7 grid grid-cols-2 gap-x-8 gap-y-6">
              <ProfileField label={a.email} value={user.email} />
              <ProfileField label={a.phone} value={user.phone} />
              <ProfileField label={a.defaultCurrency} value={currencyDisplay} />
              <ProfileField label={a.timeZone} value={timeZoneDisplay} />
            </dl>
          </div>

          <div className="h-px bg-divider w-full" />

          {/* Footer */}
          <div className="flex items-center justify-between bg-[#fcfcfc] px-8 py-5">
            <Popover.Close asChild>
              <button
                type="button"
                onClick={() => void signOut()}
                className="inline-flex items-center gap-2 text-base cursor-pointer font-medium text-[#BA1A1A] hover:opacity-80 focus-visible:outline-none"
              >
                <LogoutIcon className="size-4.5" />
                {a.logout}
              </button>
            </Popover.Close>
            {/* <IconButton
              aria-label={theme === 'dark' ? a.toLight : a.toDark}
              variant="ghost"
              icon={theme === 'dark' ? <Sun className="size-5" /> : <Moon className="size-5" />}
              onClick={() => toggle()}
              className="rounded-full"
            /> */}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

/** One label/value pair in the profile card (uppercase muted label + value). */
function ProfileField({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-1">
      <dt className="text-sm font-semibold uppercase tracking-wide text-muted">{label}</dt>
      <dd className="text-base leading-4 text-fg">{value || '—'}</dd>
    </div>
  );
}
